import { useEffect, useState } from 'react'

import { type AvailabilityReason, checkAddressAvailability } from '../native-mailbox-api'

export type AvailabilityState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'available' }
  | { status: 'unavailable'; reason: AvailabilityReason }
  | { status: 'error' }

const DEBOUNCE_MS = 400

// Live @langmail.me address availability, debounced as the user types. The auth
// service is the source of truth (format + reserved + Stalwart existence), so we
// don't duplicate its rules here beyond skipping the empty case.
export function useAddressAvailability(username: string): AvailabilityState {
  const [state, setState] = useState<AvailabilityState>({ status: 'idle' })

  useEffect(() => {
    if (!username) {
      setState({ status: 'idle' })
      return
    }

    setState({ status: 'checking' })
    const controller = new AbortController()
    const timer = setTimeout(() => {
      checkAddressAvailability(username, controller.signal)
        .then((result) => {
          setState(
            result.available ? { status: 'available' } : { status: 'unavailable', reason: result.reason ?? 'invalid' }
          )
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setState({ status: 'error' })
          }
        })
    }, DEBOUNCE_MS)

    return () => {
      controller.abort()
      clearTimeout(timer)
    }
  }, [username])

  return state
}
